import { type HttpClient, query, segment } from '../http';

import {
  type ConversationHub,
  type ConversationSuggestion,
  type ConversationPanel,
  type MessageGameSource,
  type ConversationSummary,
  type CreateGroupPayload,
  type Message,
  type MessagePage,
} from './conversations.types';

/**
 * Conversations: the hub, the thread, and what goes into them.
 *
 * @param http Transport supplied by the application.
 * @returns The `conversations` domain of the API client.
 */
export function createConversationsApi(http: HttpClient) {
  return {
    /**
     * Everything the reader is in, grouped as the hub shows it.
     *
     * @returns The household's conversation, the groups, then the direct ones.
     */
    hub: () => http.get<ConversationHub>('/conversations'),

    /**
     * How many messages the reader has not read — what the header badge polls.
     *
     * @returns The unread count.
     */
    unreadCount: () =>
      http.get<{ unread: number }>('/conversations/unread-count'),

    /**
     * One conversation, with everybody in it.
     *
     * @param id The conversation.
     * @returns Its summary.
     * @throws 403 when the reader is not in it, 404 when it is gone.
     */
    findOne: (id: string) =>
      http.get<ConversationSummary>(`/conversations/${segment(id)}`),

    /**
     * A group's side panel: who is in it and whether they are around, the
     * games they own between them, and the evenings ahead.
     *
     * One call rather than three — the panel appears whole or not at all.
     *
     * @param id The conversation.
     * @returns Its members, games and evenings.
     * @throws 403 when the reader is not in it.
     */
    panel: (id: string) =>
      http.get<ConversationPanel>(`/conversations/${segment(id)}/panel`),

    /**
     * One page of a conversation, **newest first**.
     *
     * @param id The conversation.
     * @param params Pagination; the backend caps the page at 100.
     * @returns The page and the conversation's total.
     */
    messages: (id: string, params: { page?: number; limit?: number } = {}) =>
      http.get<MessagePage>(
        `/conversations/${segment(id)}/messages${query({
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    /**
     * Writes into a conversation.
     *
     * @param id The conversation.
     * @param body What to say, up to 4000 characters.
     * @returns The message as it was stored.
     */
    send: (
      id: string,
      body: string,
      shared?: { gameId: string; source: MessageGameSource },
    ) =>
      http.post<Message>(`/conversations/${segment(id)}/messages`, {
        body,
        gameId: shared?.gameId,
        gameSource: shared?.source,
      }),

    /**
     * Puts an emoji on a message, or takes it back.
     *
     * Warning: a **toggle**. Sending the same emoji twice leaves the message
     * as it was, which is what a reaction button does everywhere.
     *
     * @param messageId The message.
     * @param emoji The emoji itself, not a name.
     * @returns The message as it now stands, reactions grouped and counted.
     * @throws 403 when the reader is not in its conversation, 404 when it is gone.
     */
    react: (messageId: string, emoji: string) =>
      http.post<Message>(
        `/conversations/messages/${segment(messageId)}/reactions`,
        { emoji },
      ),

    /**
     * Marks a conversation read up to now.
     *
     * @param id The conversation.
     * @returns Nothing.
     */
    markRead: (id: string) =>
      http.post<void>(`/conversations/${segment(id)}/read`, {}),

    /**
     * Who to write to next.
     *
     * Ranked by how much the reader and that person actually talk, each with
     * the shared conversation they are liveliest in.
     *
     * @returns The shortlist, closest first.
     */
    suggestions: () =>
      http.get<readonly ConversationSuggestion[]>('/conversations/suggestions'),

    /**
     * Pins a conversation to the reader’s own rail, or unpins it.
     *
     * Warning: the reader’s own. It moves nobody else’s rail.
     *
     * @param id The conversation.
     * @param pinned Whether it should be pinned.
     * @returns Nothing.
     */
    setPinned: (id: string, pinned: boolean) =>
      pinned
        ? http.post<void>(`/conversations/${segment(id)}/pin`, {})
        : http.delete<void>(`/conversations/${segment(id)}/pin`),

    /**
     * Opens the conversation with somebody, or finds the one already there.
     *
     * Warning: idempotent. The pair is keyed by their two ids sorted, so both
     * people land in the same conversation whichever of them opens it.
     *
     * @param userId The other person.
     * @returns The conversation, new or not.
     * @throws 400 when that person is the caller, 404 when nobody has that id.
     */
    openDirect: (userId: string) =>
      http.post<ConversationSummary>('/conversations/direct', { userId }),

    /**
     * Opens a group. The creator becomes its administrator.
     *
     * @param payload Its name and everybody else in it.
     * @returns The group.
     * @throws 400 over twenty people, 404 when one of the ids is nobody.
     */
    createGroup: (payload: CreateGroupPayload) =>
      http.post<ConversationSummary>('/conversations/groups', payload),

    /**
     * Adds a friend to a group the caller is already in.
     *
     * Warning: **friends only**, for now. Anybody in a group could otherwise
     * pull in an account the others have never heard of, and a group is a room
     * people talk in. Widening it is a product decision, not an oversight.
     *
     * @param id The group.
     * @param userId Who to add.
     * @returns The group as it now stands.
     * @throws 400 on a household or a direct conversation and past twenty
     *   people, 403 when the caller is not in it or not their friend, 409 when
     *   they are already a member.
     */
    addMember: (id: string, userId: string) =>
      http.post<ConversationSummary>(`/conversations/${segment(id)}/members`, {
        userId,
      }),
  };
}

export type ConversationsApi = ReturnType<typeof createConversationsApi>;
